import { useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import useAuth from '@/hooks/useAuth';
import {
  useCategoryLabels,
  useCategoryLabelsGrouped,
} from '@/hooks/useCategoryLabels';
import { usePlansShouldShowTabs } from '@/hooks/usePlansShouldShowTabs';
import { useScrollMemory } from '@/hooks/useScrollMemory/useScrollMemory';
import type { AddSortFields } from '@/hooks/useSortOptions';
import { useSortOptions } from '@/hooks/useSortOptions';

import { isApiError } from '@/utils/request';

import { withRoles } from '@/components/ConfirmationModal/withRoles';
import GenericError from '@/components/Error/GenericError';
import Filter, { FilterDesktop, FilterMobile } from '@/components/Filter';
import { FilterAndSortButtonProps } from '@/components/Filter/components/FilterAndSortButton';
import { FilterCheckboxSectionProps } from '@/components/Filter/components/FilterCheckboxSection';
import { FilterSearchBarProps } from '@/components/Filter/components/FilterSearchBar';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import SortFilterHeader from '@/components/SortFilterHeader/SortFilterHeader';

import AddPlan from './Modals/AddPlan';
import ChooseTemplate from './Modals/ChooseTemplate';
import CopyTemplate from './Modals/CopyTemplate';
import EnablePlan from './Modals/EnablePlan';
import InvalidName from './Modals/InvalidName';
import PlanCards from './PlanCards';

import { getOperatorsOptions, Plan } from '@/_generated/api-clients/xp';
import { useDataView } from '@/atoms/data-view';
import useGetManagePlansData from '@/routes/TrainingPlans/hooks/useGetManagePlansData';

const dataViewKey = 'manage-plans';

const ManagePlansTab = () => {
  const { t } = useTranslation();
  const { data: auth } = useAuth();
  const isLicenseeUser = auth?.isLicensee ?? false;
  const [showEnablePlanPopUp, setShowEnablePlanPopUp] = useState(false);
  const [courseId, setCourseId] = useState('');
  const [courseName, setCourseName] = useState('');
  const [showCustomPlan, setShowCustomPlan] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showCopyTemplatePopup, setShowCopyTemplatePopup] = useState(false);
  const [copiedTemplateId, setCopiedTemplatePlanId] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState<Plan | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showInvalidNameError, setShowInvalidNameError] = useState(false);
  const sortOptions = useSortOptions();

  const { data: operators } = useQuery({
    ...getOperatorsOptions(),
  });

  const { plansDataError, isFetchingPlans, refetchPlans } =
    useGetManagePlansData();

  const tabsActive = usePlansShouldShowTabs();

  useScrollMemory({
    container: 'body',
    key: dataViewKey,
    enabled: !isFetchingPlans,
  });

  const [
    {
      display: { items, total },
      filters,
      sort,
    },
    {
      setSort,
      toggleCheckboxFilter,
      setSearch,
      resetAllCheckboxFilters,
      resetSearch,
    },
  ] = useDataView<AddSortFields<Plan>>(dataViewKey);
  const categoriesValues = Object.values(filters.in.category ?? []);

  const categoryLabels = useCategoryLabels(categoriesValues, isLicenseeUser);
  const categoryLabelsGrouped = useCategoryLabelsGrouped(
    categoriesValues,
    isLicenseeUser,
  );

  if (isApiError(plansDataError)) {
    datadogRum.addError(new Error(plansDataError.message), {
      context: JSON.stringify(plansDataError),
    });
    return <GenericError />;
  }

  const onShowPopUp = (id: string, name: string) => {
    setCourseId(id);
    setCourseName(name);
    setShowEnablePlanPopUp(true);
  };

  const handlePreviewPlan = (template: Plan) => {
    setPreviewTemplate(template);
    setShowTemplateModal(false);
    setShowPreview(true);
  };

  const handleTemplateBackButton = () => {
    setPreviewTemplate(null);
    setShowTemplateModal(true);
    setShowPreview(false);
  };

  const onShowCopyPlan = (id: string) => {
    setCopiedTemplatePlanId(id);
    setShowPreview(false);
    setShowTemplateModal(false);
    setShowCopyTemplatePopup(true);
  };

  const categories: FilterCheckboxSectionProps = {
    labels: categoryLabelsGrouped,
    onToggle: value => toggleCheckboxFilter('category', value),
    title: t('Browse.categories'),
  };

  const search: FilterSearchBarProps = {
    onChange: e => {
      setSearch(e.target.value, 'nameLocalLang');
    },
    onClear: () => resetSearch(),
    placeholder: t('TrainingPlans.filtering.searchPlans'),
    value: filters.search,
  };

  const filterAndSortButtonOptions: FilterAndSortButtonProps = {
    onSortChange: setSort,
    sortOptions,
    sortValue: sortOptions.find(
      option =>
        option.key === sort?.key && option.direction === sort?.direction,
    ),
    text: `${t('TrainingPlans.filtering.show')} ${
      total
    } ${t('TrainingPlans.filtering.results')}`,
  };

  const filterOptions = {
    filterAndSortButtonOptions,
    search: <Filter.SearchBar {...search} />,
    children: <Filter.CheckboxSection {...categories} />,
  };

  const filterOptionsMobile = {
    filterAndSortButtonOptions,
    children: <Filter.CheckboxSection {...categories} />,
    search: <Filter.SearchBar {...search} />,
  };

  return (
    <>
      <LoadingOverlay isOpen={isFetchingPlans} fixed />
      <div className="relative flex max-w-full grow flex-col">
        <div className="flex w-full flex-col">
          {!isFetchingPlans && <FilterMobile {...filterOptionsMobile} />}
          {/* Top Row */}
          <div>
            <div className="flex justify-end md:pl-[304px]">
              <SortFilterHeader
                label={t('TrainingPlans.filtering.sortBy')}
                onChange={setSort}
                onClear={resetAllCheckboxFilters}
                options={sortOptions}
                showClear={false}
                showCompletedPlansOption={false}
                showMyCompletedPlansOnlySwitchValue={false}
                text={`${total} ${t('TrainingPlans.filtering.plans')}`}
                title={t('TrainingPlans.filtering.managePlans')}
                total={total}
                value={sortOptions.find(
                  option =>
                    option.key === sort?.key &&
                    option.direction === sort?.direction,
                )}
              />
            </div>
            {/* Bottom Row */}
            <div className="mt-2.5 flex justify-between">
              {/* Left Side */}
              {!isFetchingPlans && (
                <FilterDesktop {...filterOptions} tabsActive={tabsActive} />
              )}
              {/* Right Side */}
              <div className="w-full">
                <PlanCards
                  categoryLabels={categoryLabels}
                  currentFilters={Object.values(filters.in).flat()}
                  filteredAndSortedPlans={items}
                  isFetchingPlans={isFetchingPlans}
                  onShowPopUp={onShowPopUp}
                  refetchPlans={refetchPlans}
                  searchFilter={filters.search}
                  setShowCustomPlan={setShowCustomPlan}
                  setShowTemplateModal={setShowTemplateModal}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddPlan
        setShowCustomPlan={setShowCustomPlan}
        setShowInvalidNameError={setShowInvalidNameError}
        showCustomPlan={showCustomPlan}
      />
      <ChooseTemplate
        handlePreviewPlan={handlePreviewPlan}
        handleTemplateBackButton={handleTemplateBackButton}
        onShowCopyPlan={onShowCopyPlan}
        previewTemplate={previewTemplate}
        setShowPreview={setShowPreview}
        setShowTemplateModal={setShowTemplateModal}
        showPreview={showPreview}
        showTemplateModal={showTemplateModal}
      />
      <CopyTemplate
        copiedTemplateId={copiedTemplateId}
        setShowCopyTemplatePopup={setShowCopyTemplatePopup}
        setShowInvalidNameError={setShowInvalidNameError}
        setShowTemplateModal={setShowTemplateModal}
        showCopyTemplatePopup={showCopyTemplatePopup}
      />
      <InvalidName
        setShowInvalidNameError={setShowInvalidNameError}
        showInvalidNameError={showInvalidNameError}
      />
      <EnablePlan
        courseId={courseId}
        courseName={courseName}
        operators={operators ?? []}
        refetchPlans={refetchPlans}
        setShowEnablePlanPopUp={setShowEnablePlanPopUp}
        showEnablePlanPopUp={showEnablePlanPopUp}
      />
    </>
  );
};

const ManagePlansTabWithRoles = withRoles(ManagePlansTab, 'plans.manage');

export default ManagePlansTabWithRoles;
